import { releaseFlag, Tone } from "./orders";
import type { Blanket, CalendarRelease, Release } from "./types";

// What the Releases screen works out from release_calendar and blanket_balances: what needs a look on each release
// (the same flags as an order's releases, so the two screens agree), the days to show, and each open PO's cover.
// Pure, so it is tested without a browser.

/** What needs a look on a calendar row, in words, or null: the order screen's flags, plus the customer's approval. */
export function calendarFlag(r: CalendarRelease, today: string): { text: string; tone: Tone } | null {
  const asRelease = {
    status: r.status, shippedOn: null, plannedShip: r.ship ?? r.date, confirmedShip: null, closedDay: r.closedDay,
    vendorConfirmedAt: r.vendorConfirmed ? "confirmed" : null,
  } as unknown as Release;
  const flag = releaseFlag(asRelease, today, r.supplier?.name ?? "");
  if (flag) return flag;
  if (!r.customerApproved && r.status === "PROPOSED") return { text: "Customer to approve", tone: "soon" };
  return null;
}

/** The rows grouped by day, in date order (the calendar's headings). */
export function byDay(rows: CalendarRelease[]): { day: string; rows: CalendarRelease[] }[] {
  const days = new Map<string, CalendarRelease[]>();
  for (const r of [...rows].sort((a, b) => a.date.localeCompare(b.date) || a.poNumber.localeCompare(b.poNumber) || a.releaseNo - b.releaseNo)) {
    days.set(r.date, [...(days.get(r.date) ?? []), r]);
  }
  return [...days].map(([day, rs]) => ({ day, rows: rs }));
}

/** The last day of a window of `weeks` starting `from` (YYYY-MM-DD). */
export function windowEnd(from: string, weeks: number): string {
  const d = new Date(`${from}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + weeks * 7 - 1);
  return d.toISOString().slice(0, 10);
}

/** An open PO's cover in words: how far its scheduled releases reach against the weeks the customer wants. */
export function coverWords(b: Blanket): { text: string; tone: Tone } {
  const want = `${b.coverWeeksRequired} ${b.coverWeeksRequired === 1 ? "week" : "weeks"} wanted`;
  if (b.weeksScheduledAhead === null) return { text: `Nothing scheduled; ${want}`, tone: "late" };
  const ahead = `${Math.round(b.weeksScheduledAhead * 10) / 10} weeks scheduled`;
  return b.coverLow ? { text: `Short: ${ahead}, ${want}`, tone: "soon" } : { text: `${ahead}; ${want}`, tone: "good" };
}

/** Low cover first, then by customer and PO: what needs releasing comes to the top. */
export function coverOrder(rows: Blanket[]): Blanket[] {
  return [...rows].sort((a, b) => Number(b.coverLow) - Number(a.coverLow) ||
    (a.customer?.name ?? "").localeCompare(b.customer?.name ?? "") || a.poNumber.localeCompare(b.poNumber));
}
